import { useButtonClick } from '@emico-hooks/buttons'
import { testId } from '@emico-utils/datalayer'
import { overrideOptional } from '@emico-utils/overrides'
import styled from '@emotion/styled'
import { AriaButtonProps } from '@react-aria/button'
import type { PressEvent } from '@react-types/shared'
import { ElementType, useRef } from 'react'
import { useButton, mergeProps } from 'react-aria'

const isAnalyticsEnabled =
  process.env.REACT_APP_DISABLE_ANALYTICS_BUTTON_CLICK !== 'true'

export const StyledButton = styled('button', {
  shouldForwardProp: (prop) => !['as', 'isDisabled'].includes(prop.toString()),
})<Props>`
  font-family: inherit;

  &:disabled {
    cursor: not-allowed;
  }
`

export interface Props extends AriaButtonProps {
  /**
   * Tracking event name.
   * Pass an empty string to disable tracking.
   * Note that, for this to work, analyticsContext needs to be set to an empty string too
   */
  analyticsName?: string

  /**
   * Tracking event category.
   * Pass an empty string to disable tracking.
   * Note that, for this to work, analyticsName needs to be set to an empty string too
   */
  analyticsContext?: string

  /**
   * Content of the button, required because a button must have content to be accessible
   * Permitted content: Phrasing content with exception of interactive content
   * */
  children: React.ReactNode

  /**
   * Custom onClick function
   */
  onPress?(e: PressEvent): void

  /** Allow overriding the button with another component, for example, Link */
  as?: ElementType
}

export const Button = overrideOptional((props: Props) => {
  const ref = useRef<HTMLButtonElement>(null)
  const { buttonProps } = useButton(props, ref)
  const domProps = { ...props }
  const { analyticsContext, analyticsName, onPress } = props

  const handleClick = useButtonClick(analyticsName, analyticsContext, onPress)

  return (
    <StyledButton
      {...mergeProps(buttonProps, domProps)}
      ref={ref}
      data-testid={testId(analyticsName, analyticsContext)}
      onPress={isAnalyticsEnabled ? handleClick : onPress}
    >
      {props.children}
    </StyledButton>
  )
})
